import { arrayRemove, arrayUnion, deleteField, getDoc, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, } from "firebase/firestore";
import { asManagedProfileId, asTemplateId, asTripId, err, newId, notFound, ok, permissionDenied, } from "@pack-it-up/shared";
import { toDomainInviteLink, toDomainPendingInvite, toInviteLinkDto } from "../mappers/index.js";
import { logSnapshotError, toDomainError } from "./support.js";
import { callFn } from "./callable.js";
const toResourceRef = (r) => r.kind === "trip" ? { kind: "trip", id: asTripId(r.id) } : { kind: "template", id: asTemplateId(r.id) };
export const makeSharingRepo = (db, p, session, functions) => {
    const resourceDoc = (r) => (r.kind === "trip" ? p.trip(r.id) : p.template(r.id));
    return {
        // Friend-share (Doc 08 §3): the inviter already holds the target's denormalised
        // profile in their own friends subcollection, so the member entry is written
        // directly — the same shape the acceptInvite callable writes. Rules keep
        // members-map changes owner-only.
        async shareWithUser(resource, userId, role) {
            const me = session.user();
            if (me === null)
                return err(permissionDenied("share this trip"));
            try {
                const friendSnap = await getDoc(p.friend(me.id, userId));
                if (!friendSnap.exists())
                    return err(notFound("friend", userId));
                const d = friendSnap.data();
                await updateDoc(resourceDoc(resource), {
                    [`members.${userId}`]: {
                        kind: "user",
                        role,
                        profile: {
                            displayName: d.displayName ?? "",
                            email: d.email ?? "",
                            ...(d.photoUrl !== undefined ? { photoUrl: d.photoUrl } : {}),
                        },
                    },
                    memberUserIds: arrayUnion(userId),
                });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async changeRole(resource, userId, role) {
            try {
                await updateDoc(resourceDoc(resource), { [`members.${userId}.role`]: role });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async removeMember(resource, userId) {
            try {
                await updateDoc(resourceDoc(resource), { [`members.${userId}`]: deleteField(), memberUserIds: arrayRemove(userId) });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // T31 — the owner may rename any guest; a guest may rename itself (the rules carve
        // out `profile.displayName` on your own guest entry via `onlyOwnGuestNameChanged`).
        // Anyone else is denied at the rules layer.
        async renameGuest(resource, userId, displayName) {
            try {
                await updateDoc(resourceDoc(resource), { [`members.${userId}.profile.displayName`]: displayName });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // Email invites write the admin-only pendingInvites collection (T12).
        // Writes the admin-only pendingInvites collection + sends mail — a Cloud Function (T12).
        async inviteByEmail(resource, email, role) {
            const r = await callFn(functions, "inviteByEmail", { resource, email, role });
            return r.ok ? ok(undefined) : r;
        },
        observeMyPendingInvites: (resource) => ({
            subscribe(next) {
                const u = session.user();
                if (!u) {
                    next([]);
                    return (() => { });
                }
                // Rules only allow listing invites you sent (resource.data.invitedBy == uid),
                // so the query must carry the invitedBy filter for the list to be provable.
                const q = query(p.pendingInvites(), where("invitedBy", "==", u.id), where("resource.kind", "==", resource.kind), where("resource.id", "==", resource.id), where("status", "==", "pending"));
                const unsub = onSnapshot(q, (snap) => {
                    const out = [];
                    for (const d of snap.docs) {
                        const r = toDomainPendingInvite(d.data());
                        if (r.ok)
                            out.push(r.value);
                    }
                    next(out);
                }, logSnapshotError("pendingInvites"));
                return unsub;
            },
        }),
        // pendingInvites is callable-write-only (T11 rules), so revoke goes through the
        // cancelInvite Cloud Function.
        async revokePendingInvite(inviteId) {
            const r = await callFn(functions, "cancelInvite", { inviteId });
            return r.ok ? ok(undefined) : r;
        },
        // Invite inbox (T25): pendingInvites is admin-only, so the invitee's view of
        // their own invites goes through callables end-to-end.
        async listIncomingInvites() {
            const r = await callFn(functions, "listMyInvites", {});
            if (!r.ok)
                return r;
            return ok(r.value.invites.map((v) => ({
                id: v.id,
                resource: toResourceRef(v.resource),
                role: v.role,
                resourceName: v.resourceName ?? "",
                inviterName: v.inviterName ?? "",
            })));
        },
        async acceptInvite(inviteId) {
            const r = await callFn(functions, "acceptInvite", { inviteId });
            return r.ok ? ok(undefined) : r;
        },
        async declineInvite(inviteId) {
            const r = await callFn(functions, "declineInvite", { inviteId });
            return r.ok ? ok(undefined) : r;
        },
        async addManagedMember(resource, profileId) {
            try {
                const mp = await getDoc(p.managedProfile(profileId));
                if (!mp.exists())
                    return err(notFound("managedProfile", profileId));
                await updateDoc(resourceDoc(resource), {
                    [`members.${profileId}`]: {
                        kind: "managed",
                        guardianUserIds: mp.get("guardianUserIds") ?? [],
                        profile: { displayName: mp.get("displayName") },
                    },
                });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // Trip-scoped managed member: minted straight into the resource's members map with
        // no top-level managedProfiles doc; the creator is its guardian.
        async addManagedMemberByName(resource, displayName) {
            const u = session.user();
            if (!u)
                return err(permissionDenied("add a member"));
            try {
                const id = asManagedProfileId(newId());
                await updateDoc(resourceDoc(resource), {
                    [`members.${id}`]: { kind: "managed", guardianUserIds: [u.id], profile: { displayName } },
                });
                return ok(id);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        async createInviteLink(resource, role, opts) {
            const u = session.user();
            if (!u)
                return err(toDomainError({ code: "permission-denied" }));
            try {
                const token = newId();
                const link = {
                    token,
                    resource,
                    role,
                    allowJoin: opts?.allowJoin ?? false,
                    createdBy: u.id,
                    revoked: false,
                    ...(opts?.expiresAt !== undefined ? { expiresAt: opts.expiresAt } : {}),
                    createdAt: new Date(),
                };
                await setDoc(p.shareLink(token), { ...toInviteLinkDto(link), createdAt: serverTimestamp() });
                return ok(link);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        observeInviteLinks: (resource) => ({
            subscribe(next) {
                const q = query(p.shareLinks(), where("resourceType", "==", resource.kind), where("resourceId", "==", resource.id), where("revoked", "==", false));
                const unsub = onSnapshot(q, (snap) => {
                    const out = [];
                    for (const d of snap.docs) {
                        const r = toDomainInviteLink(d.data());
                        if (r.ok)
                            out.push(r.value);
                    }
                    next(out);
                }, logSnapshotError("inviteLinks"));
                return unsub;
            },
        }),
        async revokeInviteLink(token) {
            try {
                await updateDoc(p.shareLink(token), { revoked: true });
                return ok(undefined);
            }
            catch (e) {
                return err(toDomainError(e));
            }
        },
        // Reading/joining a link needs an admin read of the link + membership mint (T12).
        // Redeeming a link needs an admin read of the link + grant/member mint — callables (T12).
        async viewViaLink(token) {
            const r = await callFn(functions, "viewViaLink", { token });
            return r.ok ? ok({ resource: toResourceRef(r.value.resource), allowJoin: r.value.allowJoin ?? true }) : r;
        },
        async joinViaLink(token, displayName) {
            const r = await callFn(functions, "joinViaLink", {
                token,
                ...(displayName !== undefined ? { displayName } : {}),
            });
            return r.ok ? ok(toResourceRef(r.value.resource)) : r;
        },
    };
};
//# sourceMappingURL=sharing.repo.js.map